import React from 'react';
import { AlertTriangle, Ban, Lock, SearchX, ServerCrash, X } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { ApiError } from '@/utils/apiError';

// ─── Rótulos dos campos ───────────────────────────────────────────────────────
//
// O backend devolve o nome do campo em snake_case (tag JSON do DTO). O mapa
// traduz para o rótulo que o usuário vê no formulário; campo sem tradução cai
// no próprio nome, que ainda é melhor do que esconder o erro.

const API_FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  email: 'E-mail',
  board_id: 'Quadro',
  board_column_id: 'Coluna',
  board_type: 'Tipo do quadro',
  title: 'Título',
  description: 'Descrição',
  priority: 'Prioridade',
  start_date: 'Início',
  end_date: 'Fim',
  due_date: 'Prazo',
  start_time: 'Hora de início',
  end_time: 'Hora de fim',
  color: 'Cor',
  wip_limit: 'Limite WIP',
  frequency: 'Frequência',
  interval_count: 'Intervalo',
  days_of_week: 'Dias da semana',
  day_of_month: 'Dia do mês',
  lead_days: 'Antecedência (dias)',
  column_ids: 'Colunas',
  is_active: 'Ativo',
  // Documentos
  folder_id: 'Pasta',
  parent_folder_id: 'Pasta superior',
  file_name: 'Arquivo',
  content_type: 'Tipo do arquivo',
  size_bytes: 'Tamanho',
  sha256: 'Assinatura (SHA-256)',
  doc_date: 'Data do documento',
  expires_at: 'Validade',
  remind_days: 'Aviso (dias)',
  tags: 'Tags',
  ref_type: 'Vínculo',
  ref_id: 'Item vinculado',
  // Acervo CAC
  kind: 'Tipo',
  brand: 'Marca',
  model: 'Modelo',
  caliber: 'Calibre',
  serial_number: 'Nº de série',
  sigma_number: 'SIGMA',
  acquisition_date: 'Data de aquisição',
  acquisition_value_cents: 'Valor de aquisição',
  status: 'Situação',
  notes: 'Observações',
  doc_type: 'Tipo do documento',
  firearm_id: 'Arma',
  number: 'Número',
  issuer_unit: 'Órgão emissor',
  issued_at: 'Emissão',
  document_id: 'Arquivo',
  // Administração
  cac_enabled: 'Acervo CAC',
  cac_enabled_for_all: 'Liberar Acervo CAC para todos',
  plan: 'Plano',
  access_until: 'Acesso até',
  signup_enabled: 'Cadastro aberto',
  trial_days: 'Dias de teste',
  days: 'Dias',
  limit: 'Limite',
  // Finanças
  account_type: 'Tipo da conta',
  bank_code: 'Código do banco',
  bank_name: 'Banco',
  branch: 'Agência',
  balance_date: 'Data do saldo',
  amount_cents: 'Valor',
  movement: 'Movimento',
  parent_category_id: 'Categoria pai',
  icon: 'Ícone',
  is_disabled: 'Desativada',
  account_id: 'Conta',
  transaction_date: 'Data',
  category_id: 'Categoria',
  first_date: 'Primeira parcela',
  total_cents: 'Valor total',
  installments: 'Nº de parcelas',
  from_account_id: 'Conta de origem',
  to_account_id: 'Conta de destino',
  target_type: 'Tipo de destino',
  target_id: 'Conta',
  import_layout_id: 'Layout',
  file: 'Arquivo',
  file_format: 'Formato do arquivo',
  delimiter: 'Delimitador',
  has_header: 'Cabeçalho',
  skip_rows: 'Linhas a pular',
  date_column: 'Coluna da data',
  description_column: 'Coluna da descrição',
  amount_column: 'Coluna do valor',
  credit_column: 'Coluna do crédito',
  date_format: 'Formato da data',
  decimal_comma: 'Vírgula decimal',
  invert_sign: 'Inverter sinal',
  pattern: 'Padrão',
  applies_to: 'Aplica-se a',
  chosen_category_id: 'Categoria',
};

/**
 * Traduz `items[2].sku` para `Item 3 · SKU`, mantendo o índice legível para o
 * usuário (base 1) — a validação do backend usa `dive`, que indexa a partir de 0.
 */
const labelFor = (field: string, extra?: Record<string, string>): string => {
  const labels = extra ? { ...API_FIELD_LABELS, ...extra } : API_FIELD_LABELS;
  if (labels[field]) return labels[field];

  const indexed = field.match(/^(\w+)\[(\d+)\]\.(.+)$/);
  if (indexed) {
    const [, collection, index, leaf] = indexed;
    const collectionLabel = collection === 'items' ? 'Item'
      : collection === 'installments' ? 'Parcela'
      : labels[collection] ?? collection;
    return `${collectionLabel} ${Number(index) + 1} · ${labels[leaf] ?? leaf}`;
  }

  return field;
};

// ─── Aparência por tipo de erro ───────────────────────────────────────────────

type Tone = {
  title: string;
  icon: React.ReactNode;
  header: string;
  heading: string;
};

const toneFor = (status: number): Tone => {
  switch (status) {
    case 400:
      return {
        title: 'Revise os dados do formulário',
        icon: <AlertTriangle size={20} className="text-amber-600" />,
        header: 'border-amber-200 bg-amber-50',
        heading: 'text-amber-900',
      };
    case 401:
    case 403:
      return {
        title: status === 401 ? 'Sessão expirada' : 'Acesso negado',
        icon: status === 401
          ? <Lock size={20} className="text-slate-600" />
          : <Ban size={20} className="text-red-600" />,
        header: status === 401 ? 'border-slate-200 bg-slate-50' : 'border-red-200 bg-red-50',
        heading: status === 401 ? 'text-slate-900' : 'text-red-900',
      };
    case 404:
      return {
        title: 'Registro não encontrado',
        icon: <SearchX size={20} className="text-slate-600" />,
        header: 'border-slate-200 bg-slate-50',
        heading: 'text-slate-900',
      };
    case 409:
      return {
        title: 'Conflito com o estado atual',
        icon: <AlertTriangle size={20} className="text-amber-600" />,
        header: 'border-amber-200 bg-amber-50',
        heading: 'text-amber-900',
      };
    default:
      return {
        title: 'Não foi possível concluir',
        icon: <ServerCrash size={20} className="text-red-600" />,
        header: 'border-red-200 bg-red-50',
        heading: 'text-red-900',
      };
  }
};

// ─── Modal ────────────────────────────────────────────────────────────────────

interface ApiErrorModalProps {
  /** Erro capturado no `catch`; `null` mantém o modal fechado. */
  error: unknown;
  onClose: () => void;
  /** Título fixo, quando a tela quiser sobrepor o padrão do status. */
  title?: string;
  /** Rótulos adicionais/sobrescritos para campos específicos da tela. */
  fieldLabels?: Record<string, string>;
}

/**
 * Modal único para erros de chamada de API. Entende o formato de erro do planbackgo
 * (`ApiError`), listando cada campo rejeitado, e degrada para uma mensagem
 * simples com qualquer outro `Error` — inclusive falha de rede.
 */
export const ApiErrorModal: React.FC<ApiErrorModalProps> = ({ error, onClose, title, fieldLabels }) => {
  if (!error) return null;

  const apiError = error instanceof ApiError ? error : null;
  // 403 "access_expired": a tela de acesso encerrado (AccessExpiredView) já está na frente.
  if (apiError?.isAccessExpired) return null;
  const status = apiError?.status ?? 0;
  const details = apiError?.details;
  const tone = toneFor(status);
  const message = error instanceof Error ? error.message : String(error);

  return (
    <Modal isOpen onClose={onClose} size="md" showCloseButton={false}>
      <div className={`flex items-center justify-between px-6 py-4 border-b rounded-t-lg ${tone.header}`}>
        <div className="flex items-center gap-2">
          {tone.icon}
          <h2 className={`text-lg font-bold ${tone.heading}`}>{title ?? tone.title}</h2>
        </div>
        <button
          onClick={onClose}
          aria-label="Fechar"
          className="p-1.5 rounded-full hover:bg-black/5 transition-colors"
        >
          <X size={18} className="text-slate-500" />
        </button>
      </div>

      <div className="px-6 py-4 space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
        <p className="text-sm text-slate-600">{message}</p>

        {details && (
          <ul className="space-y-1.5">
            {Object.entries(details).map(([field, msg]) => (
              <li key={field} className="flex items-start gap-2 text-sm">
                <AlertTriangle size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <span>
                  <span className="font-medium text-slate-800">{labelFor(field, fieldLabels)}:</span>{' '}
                  <span className="text-slate-600">{msg}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end">
        <Button variant="secondary" onClick={onClose}>Fechar</Button>
      </div>
    </Modal>
  );
};

export default ApiErrorModal;
